import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, MapPin, Compass, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import Shell from "@/components/Shell";
import SearchWidget from "@/components/SearchWidget";

const HERO = "https://images.unsplash.com/photo-1619796696652-a29a854f21a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }) };

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/experiences?featured=true").then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  return (
    <Shell>
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Himalayan valley" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/50 via-primary/20 to-background" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-40 md:pt-32 md:pb-48">
          <motion.span initial="hidden" animate="show" variants={fade} className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 backdrop-blur px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.35em] text-white">
            <Compass size={14} /> Dare To Rise
          </motion.span>
          <motion.h1 custom={1} initial="hidden" animate="show" variants={fade} className="font-display font-semibold text-6xl sm:text-7xl lg:text-8xl leading-[0.88] tracking-tighter mt-6 text-white max-w-4xl drop-shadow-2xl">
            Chase horizons.<br /><span className="italic font-normal text-accent">Find yourself.</span>
          </motion.h1>
          <motion.p custom={2} initial="hidden" animate="show" variants={fade} className="mt-7 text-lg sm:text-xl font-light text-white/90 max-w-2xl leading-relaxed tracking-wide">
            Sacred trails, hidden valleys, mountain stays and wild adventures across India — with flights, hotels, buses and cabs, all booked in one place.
          </motion.p>
        </div>
        {/* Search widget overlapping */}
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 -mt-28 md:-mt-24 pb-4">
          <div className="mb-3 flex justify-center" data-testid="realtime-booking-note">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/80 border border-white/25 backdrop-blur px-4 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-lg">
              <Sparkles size={14} className="text-accent" /> Real-Time Booking — Very soon on Zatayoo
            </span>
          </div>
          <SearchWidget />
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Discover by vibe</span>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground mt-2">What are you chasing?</h2>
          </div>
          <Link to="/explore" className="inline-flex items-center gap-2 text-accent font-semibold hover:gap-3 transition-all">All experiences <ArrowRight size={18} /></Link>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cats.map((c, i) => (
            <motion.div key={c.slug} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
              <Link to={`/explore?category=${c.slug}`} data-testid={`cat-${c.slug}`}
                className="group relative block h-52 rounded-2xl overflow-hidden border border-primary/10">
                <img src={c.image} alt={c.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-5">
                  <h3 className="font-display text-2xl text-white">{c.label}</h3>
                  <p className="text-white/80 text-sm">{c.tagline} · {c.count} trips</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground">Hand-picked right now</h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((e, i) => (
            <motion.div key={e.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
              <Link to={`/experience/${e.id}`} className="group block rounded-2xl overflow-hidden border border-primary/10 bg-card hover:-translate-y-1 transition-transform" data-testid={`featured-${i}`}>
                <div className="h-56 overflow-hidden relative">
                  <img src={e.image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 rounded-full bg-white/90 text-primary text-xs font-semibold px-3 py-1">{e.category_label}</span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={12} /> {e.location}</div>
                  <h3 className="font-semibold text-lg mt-1 text-foreground">{e.title}</h3>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-primary/10">
                    <span className="flex items-center gap-1 text-sm text-foreground"><Star size={14} className="fill-accent text-accent" /> {e.rating}</span>
                    <span className="font-display text-xl text-foreground">{inr(e.price)}<span className="text-xs text-muted-foreground font-sans"> /person</span></span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* OUR STORY teaser */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="rounded-[2rem] border border-primary/10 bg-secondary/40 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Zatayoo · Dare To Rise</span>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-foreground mt-2">A proudly Indian initiative, bringing technology to travel.</h2>
            <p className="text-muted-foreground mt-3 text-lg">Founded by <b className="text-foreground">Mr. Prakash Singh</b> — the "Yatra King" — on a mission to reveal hidden India and lift the communities you visit.</p>
          </div>
          <Link to="/about" className="inline-flex items-center gap-2 rounded-full border border-primary/20 px-6 py-3.5 font-semibold text-foreground hover:bg-secondary transition-colors whitespace-nowrap" data-testid="home-about-link">
            Our story <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="rounded-[2rem] bg-primary text-primary-foreground p-10 md:p-16">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight max-w-2xl">Your next story is somewhere off the map.</h2>
          <p className="mt-4 text-primary-foreground/70 text-lg max-w-lg">Book an experience, then add flights, a stay and a cab — all in one place.</p>
          <Link to="/explore" className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-7 py-4 font-semibold mt-8 hover:-translate-y-px transition-transform" data-testid="cta-explore">
            Start exploring <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </Shell>
  );
}
